import { useState, useCallback, useEffect } from 'react';
import { userService } from '@modules/users/services/userService';
import useModal from '@shared/hooks/useModal';
import useFetchData from '@shared/hooks/useFetchData';
import { useDeleteHandler } from '@shared/hooks/useDeleteHandler';
import { notifyError } from '@config/tostifyConfig/tostifyConfig';
import { useNavigate } from 'react-router-dom';

export const useUsersTable = () => {
    const { modalState, handleOpenModal, handleCloseModal } = useModal();
    const getData = userService.getUsers;
    const navigate = useNavigate();
    const defaultSortBy = 'username';

    const searchOptions = [
        { key: 'username', label: 'username' },
        { key: 'accountname', label: 'accountname' }
    ];

    const {
        data,
        loading,
        error,
        pageNumber,
        selectedData,
        setSelectedData,
        pageSize,
        searchQuery,
        totalPages,
        totalElements,
        setSearchQuery,
        handleSearchClick,
        fetchData,
        handleSort,
        sortBy,
        sortDir,
        setPageSize,
        setpageNumber,
        handleCheckboxChange,
        handleError
    } = useFetchData();

    const handleFetchError = useCallback((error) => {
        console.error('Error fetching users:', error);
        handleError(error?.message || 'Failed to fetch users');
    }, [handleError]);

    useEffect(() => {
        fetchData(userService.getUsers, {
            searchQuery: searchQuery,
            sortBy: sortBy || defaultSortBy,
            sortDir: sortDir
        });
    }, [pageNumber, pageSize, sortBy, sortDir]);

    const handleConfirmDelete = useDeleteHandler({
        service: userService,
        entityName: 'User',
        selectedData,
        fetchData,
        setSelectedData,
        handleCloseModal,
    });

    const handleSaveGroup = async (selectedGroups) => {
        try {
            await userService.updateUserGroups(selectedData, selectedGroups);
            await fetchData(userService.getUsers, {
                searchQuery: searchQuery,
                sortBy: sortBy || defaultSortBy,
                sortDir: sortDir
            });
            handleCloseModal();
        } catch (error) {
            handleFetchError(error);
        }
    };

    const handleEditUser = useCallback(() => {
        if (!selectedData) {
            notifyError('No user selected for editing');
            return;
        }

        handleOpenModal('edit');
    }, [selectedData, handleOpenModal]);

    const handleAddCloseModal = useCallback(() => {
        handleCloseModal();
        fetchData(userService.getUsers, {
            searchQuery: searchQuery,
            sortBy: sortBy || defaultSortBy,
            sortDir: sortDir
        });
    }, [handleCloseModal, fetchData, searchQuery, sortBy, sortDir]);

    const selectedUserName = selectedData !== null
        ? data?.find(user => user.id === selectedData)?.username
        : '';

    return {
        data,
        loading,
        error,
        getData,
        selectedData,
        modalState,
        pageNumber,
        pageSize,
        sortBy,
        sortDir,
        totalElements,
        totalPages,
        searchQuery,
        searchOptions,
        
        handleSaveGroup,
        handleOpenModal,
        handleCloseModal,
        handleAddCloseModal,
        handleEditUser,
        handleConfirmDelete,
        handleSearchClick,
        handleCheckboxChange,
        handleSort,
        setSearchQuery,
        setPageSize,
        setpageNumber,
        setSelectedData,
        selectedUserName,
        fetchData,
        userService
    };
}; 